import { SortingState } from "@tanstack/react-table"

import { ScheduleHorizon } from "@constants/scheduleHorizon"

export type CardsViewConfig = {
    sorting: SortingState
    attributeVisibility: Record<string, boolean>
    scheduleHorizon?: ScheduleHorizon
}

export type CardsViewConfigType = {
    version?: string
    cards: {
        [key: string]: CardsViewConfig | undefined
    }
}

const defaultCardsViewConfig: CardsViewConfigType = {
    /*
        Versioning Scheme:
        - Major: Breaking changes.
        - Minor: Attribute additions/removals.
        - Patch: Updates to the default config.
        Note: Patch updates are only applied to users without modified view configs, serving as a new default without data migration.
    */
    version: "1.0.1",
    cards: {
        // Jobs
        jobs_cards__open_all_open: {
            sorting: [{ id: "start_time", desc: false }],
            attributeVisibility: {
                service_name: true,
                labels: true,
                job_type: true,
                priority: true,
                origin_type_label: true,
                assigned_technicians: false,
                client_name: false,
                service_location_name: false,
                service_location_address: false,
                start_time: false,
                duration: false,
            },
            scheduleHorizon: "all_dates",
        },
        jobs_cards__open_pending: {
            sorting: [{ id: "id", desc: true }],
            attributeVisibility: {
                service_name: true,
                labels: true,
                job_type: true,
                priority: true,
                origin_type_label: true,
                assigned_technicians: false,
                client_name: false,
                service_location_name: false,
                service_location_address: false,
                start_time: false,
                duration: false,
            },
            scheduleHorizon: "all_dates",
        },
        jobs_cards__open_scheduled: {
            sorting: [{ id: "start_time", desc: false }],
            attributeVisibility: {
                service_name: true,
                labels: true,
                job_type: true,
                priority: true,
                origin_type_label: true,
                assigned_technicians: false,
                client_name: false,
                service_location_name: false,
                service_location_address: false,
                start_time: false,
                duration: false,
            },
            scheduleHorizon: "all_dates",
        },
        jobs_cards__open_in_progress: {
            sorting: [{ id: "start_time", desc: false }],
            attributeVisibility: {
                service_name: true,
                labels: true,
                job_type: true,
                priority: true,
                origin_type_label: true,
                assigned_technicians: false,
                client_name: false,
                service_location_name: false,
                service_location_address: false,
                start_time: false,
                duration: false,
            },
            scheduleHorizon: "all_dates",
        },
        jobs_cards__open_on_hold: {
            sorting: [{ id: "start_time", desc: false }],
            attributeVisibility: {
                service_name: true,
                labels: true,
                job_type: true,
                priority: true,
                origin_type_label: true,
                assigned_technicians: false,
                client_name: false,
                service_location_name: false,
                service_location_address: false,
                start_time: false,
                duration: false,
            },
            scheduleHorizon: "all_dates",
        },
        jobs_cards__open_completed: {
            sorting: [{ id: "last_completed_at", desc: true }],
            attributeVisibility: {
                service_name: true,
                labels: true,
                job_type: true,
                priority: true,
                origin_type_label: true,
                assigned_technicians: false,
                client_name: false,
                service_location_name: false,
                service_location_address: false,
                start_time: false,
                duration: false,
            },
            scheduleHorizon: "all_dates",
        },
        jobs_cards__closed_all_closed: {
            sorting: [{ id: "id", desc: true }],
            attributeVisibility: {
                service_name: true,
                labels: true,
                job_type: true,
                priority: true,
                origin_type_label: true,
                assigned_technicians: false,
                client_name: false,
                service_location_name: false,
                service_location_address: false,
                start_time: false,
                duration: false,
            },
        },
        jobs_cards__closed_finalized: {
            sorting: [{ id: "last_finalized_at", desc: true }],
            attributeVisibility: {
                service_name: true,
                labels: true,
                job_type: true,
                priority: true,
                origin_type_label: true,
                assigned_technicians: false,
                client_name: false,
                service_location_name: false,
                service_location_address: false,
                start_time: false,
                duration: false,
            },
        },
        jobs_cards__closed_cancelled: {
            sorting: [{ id: "start_time", desc: true }],
            attributeVisibility: {
                service_name: true,
                labels: true,
                job_type: true,
                priority: true,
                origin_type_label: true,
                assigned_technicians: false,
                client_name: false,
                service_location_name: false,
                service_location_address: false,
                start_time: false,
                duration: false,
            },
        },

        // Estimates
        estimates_cards__open_all_open: {
            sorting: [{ id: "date_due", desc: false }],
            attributeVisibility: {
                client_name: true,
                service_name: true,
                service_location_name: true,
                service_location_address: true,
                labels: true,
                total: false,
                date_due: false,
                date_issued: false,
            },
        },
        estimates_cards__open_pending: {
            sorting: [{ id: "date_due", desc: false }],
            attributeVisibility: {
                client_name: true,
                service_name: true,
                service_location_name: true,
                service_location_address: true,
                labels: true,
                total: false,
                date_due: false,
                date_issued: false,
            },
        },
        estimates_cards__open_sent: {
            sorting: [{ id: "date_due", desc: false }],
            attributeVisibility: {
                client_name: true,
                service_name: true,
                service_location_name: true,
                service_location_address: true,
                labels: true,
                total: false,
                date_due: false,
                date_issued: false,
            },
        },
        estimates_cards__open_approved: {
            sorting: [{ id: "date_due", desc: false }],
            attributeVisibility: {
                client_name: true,
                service_name: true,
                service_location_name: true,
                service_location_address: true,
                labels: true,
                total: false,
                date_due: false,
                date_issued: false,
            },
        },
        estimates_cards__closed_all_closed: {
            sorting: [{ id: "date_due", desc: true }],
            attributeVisibility: {
                client_name: true,
                service_name: true,
                service_location_name: true,
                service_location_address: true,
                labels: true,
                total: false,
                date_due: false,
                date_issued: false,
            },
        },
        estimates_cards__closed_won: {
            sorting: [{ id: "date_due", desc: true }],
            attributeVisibility: {
                client_name: true,
                service_name: true,
                service_location_name: true,
                service_location_address: true,
                labels: true,
                total: false,
                date_due: false,
                date_issued: false,
            },
        },
        estimates_cards__closed_lost: {
            sorting: [{ id: "date_due", desc: true }],
            attributeVisibility: {
                client_name: true,
                service_name: true,
                service_location_name: true,
                service_location_address: true,
                labels: true,
                total: false,
                date_due: false,
                date_issued: false,
            },
        },
        estimates_cards__closed_cancelled: {
            sorting: [{ id: "date_due", desc: true }],
            attributeVisibility: {
                client_name: true,
                service_name: true,
                service_location_name: true,
                service_location_address: true,
                labels: true,
                total: false,
                date_due: false,
                date_issued: false,
            },
        },

        // Invoices
        invoices_cards__open_all_open: {
            sorting: [{ id: "date_due", desc: false }],
            attributeVisibility: {
                client_name: true,
                amount_due: true,
                total: false,
                service_name: true,
                service_location_name: true,
                service_location_address: true,
                labels: false,
                date_due: false,
                date_issued: false,
            },
        },
        invoices_cards__open_pending: {
            sorting: [{ id: "date_due", desc: false }],
            attributeVisibility: {
                client_name: true,
                amount_due: true,
                total: false,
                service_name: true,
                service_location_name: true,
                service_location_address: true,
                labels: false,
                date_due: false,
                date_issued: false,
            },
        },
        invoices_cards__open_outstanding: {
            sorting: [{ id: "date_due", desc: false }],
            attributeVisibility: {
                client_name: true,
                amount_due: true,
                total: false,
                service_name: true,
                service_location_name: true,
                service_location_address: true,
                labels: false,
                date_due: false,
                date_issued: false,
            },
        },
        invoices_cards__open_overdue: {
            sorting: [{ id: "date_due", desc: false }],
            attributeVisibility: {
                client_name: true,
                amount_due: true,
                total: false,
                service_name: true,
                service_location_name: true,
                service_location_address: true,
                labels: false,
                date_due: false,
                date_issued: false,
            },
        },
        invoices_cards__closed_all_closed: {
            sorting: [{ id: "date_due", desc: true }],
            attributeVisibility: {
                client_name: true,
                amount_due: false,
                total: true,
                service_name: true,
                service_location_name: true,
                service_location_address: true,
                labels: false,
                date_due: false,
                date_issued: false,
            },
        },
        invoices_cards__closed_paid: {
            sorting: [{ id: "date_due", desc: true }],
            attributeVisibility: {
                client_name: true,
                amount_due: false,
                total: true,
                service_name: true,
                service_location_name: true,
                service_location_address: true,
                labels: false,
                date_due: false,
                date_issued: false,
            },
        },
        invoices_cards__closed_voided: {
            sorting: [{ id: "date_due", desc: true }],
            attributeVisibility: {
                client_name: true,
                amount_due: false,
                total: true,
                service_name: true,
                service_location_name: true,
                service_location_address: true,
                labels: false,
                date_due: false,
                date_issued: false,
            },
        },
        invoices_cards__closed_forgiven: {
            sorting: [{ id: "date_due", desc: true }],
            attributeVisibility: {
                client_name: true,
                amount_due: false,
                total: true,
                service_name: true,
                service_location_name: true,
                service_location_address: true,
                labels: false,
                date_due: false,
                date_issued: false,
            },
        },

        // Purchase Orders
        purchase_orders_cards__open_all_open: {
            sorting: [{ id: "date_issued", desc: false }],
            attributeVisibility: {
                vendor_name: true,
                labels: true,
                item_count: true,
                subtotal: true,
                shipping: true,
                preferred_shipping: false,
                date_issued: false,
                estimated_arrival_time: false,
                payment_terms: false,
            },
        },
        purchase_orders_cards__open_pending: {
            sorting: [{ id: "date_issued", desc: false }],
            attributeVisibility: {
                vendor_name: true,
                labels: true,
                item_count: true,
                subtotal: true,
                shipping: false,
                preferred_shipping: true,
                date_issued: false,
                estimated_arrival_time: false,
                payment_terms: false,
            },
        },
        purchase_orders_cards__open_open: {
            sorting: [{ id: "date_issued", desc: false }],
            attributeVisibility: {
                vendor_name: true,
                labels: true,
                item_count: true,
                subtotal: true,
                shipping: false,
                preferred_shipping: true,
                date_issued: false,
                estimated_arrival_time: false,
                payment_terms: false,
            },
        },
        purchase_orders_cards__open_in_transit: {
            sorting: [{ id: "date_issued", desc: false }],
            attributeVisibility: {
                vendor_name: true,
                labels: true,
                item_count: true,
                subtotal: true,
                shipping: false,
                preferred_shipping: false,
                date_issued: false,
                estimated_arrival_time: true,
                payment_terms: false,
            },
        },
        purchase_orders_cards__open_delivered: {
            sorting: [{ id: "date_issued", desc: false }],
            attributeVisibility: {
                vendor_name: true,
                labels: true,
                item_count: true,
                subtotal: true,
                shipping: true,
                preferred_shipping: false,
                date_issued: false,
                estimated_arrival_time: false,
                payment_terms: false,
            },
        },
        purchase_orders_cards__open_stocked: {
            sorting: [{ id: "date_issued", desc: false }],
            attributeVisibility: {
                vendor_name: true,
                labels: true,
                item_count: true,
                subtotal: true,
                shipping: false,
                preferred_shipping: false,
                date_issued: false,
                estimated_arrival_time: false,
                payment_terms: true,
            },
        },
        purchase_orders_cards__closed_all_closed: {
            sorting: [{ id: "date_issued", desc: false }],
            attributeVisibility: {
                vendor_name: true,
                labels: true,
                item_count: true,
                subtotal: true,
                shipping: false,
                preferred_shipping: false,
                date_issued: true,
                estimated_arrival_time: false,
                payment_terms: false,
            },
        },
        purchase_orders_cards__closed_paid: {
            sorting: [{ id: "date_issued", desc: false }],
            attributeVisibility: {
                vendor_name: true,
                labels: true,
                item_count: true,
                subtotal: true,
                shipping: false,
                preferred_shipping: false,
                date_issued: true,
                estimated_arrival_time: false,
                payment_terms: false,
            },
        },
        purchase_orders_cards__closed_cancelled: {
            sorting: [{ id: "date_issued", desc: false }],
            attributeVisibility: {
                vendor_name: true,
                labels: true,
                item_count: true,
                subtotal: true,
                shipping: false,
                preferred_shipping: false,
                date_issued: true,
                estimated_arrival_time: false,
                payment_terms: false,
            },
        },

        // Pricebook Items - Services
        pricebook_items_services_cards__active_all_active: {
            sorting: [
                { id: "confirmed", desc: false },
                { id: "description", desc: false },
            ],
            attributeVisibility: {
                description: true,
                subtype_label: true,
                summary_template: true,
                cost: true,
                default_price: true,
                default_job_duration: false,
                labels: false,
                last_modified_at: false,
            },
        },
        pricebook_items_services_cards__active_review: {
            sorting: [{ id: "description", desc: false }],
            attributeVisibility: {
                description: true,
                subtype_label: true,
                summary_template: true,
                cost: true,
                default_price: true,
                default_job_duration: false,
                labels: false,
                last_modified_at: false,
            },
        },
        pricebook_items_services_cards__active_confirmed: {
            sorting: [{ id: "description", desc: false }],
            attributeVisibility: {
                description: true,
                subtype_label: true,
                summary_template: true,
                cost: true,
                default_price: true,
                default_job_duration: false,
                labels: false,
                last_modified_at: false,
            },
        },
        pricebook_items_services_cards__inactive_all_inactive: {
            sorting: [
                { id: "confirmed", desc: false },
                { id: "description", desc: false },
            ],
            attributeVisibility: {
                description: true,
                subtype_label: true,
                summary_template: true,
                cost: true,
                default_price: true,
                default_job_duration: false,
                labels: false,
                last_modified_at: false,
            },
        },

        // Pricebook Items - Parts & Materials
        "pricebook_items_parts_&_materials_cards__active_all_active": {
            sorting: [
                { id: "confirmed", desc: false },
                { id: "description", desc: false },
            ],
            attributeVisibility: {
                description: true,
                subtype_label: true,
                manufacturer: false,
                part_number: false,
                cost: true,
                default_price: true,
                labels: true,
                last_modified_at: false,
            },
        },
        "pricebook_items_parts_&_materials_cards__active_review": {
            sorting: [{ id: "description", desc: false }],
            attributeVisibility: {
                description: true,
                subtype_label: true,
                manufacturer: false,
                part_number: false,
                cost: true,
                default_price: true,
                labels: true,
                last_modified_at: false,
            },
        },
        "pricebook_items_parts_&_materials_cards__active_confirmed": {
            sorting: [{ id: "description", desc: false }],
            attributeVisibility: {
                description: true,
                subtype_label: true,
                manufacturer: false,
                part_number: false,
                cost: true,
                default_price: true,
                labels: true,
                last_modified_at: false,
            },
        },
        "pricebook_items_parts_&_materials_cards__inactive_all_inactive": {
            sorting: [
                { id: "confirmed", desc: false },
                { id: "description", desc: false },
            ],
            attributeVisibility: {
                description: true,
                subtype_label: true,
                manufacturer: false,
                part_number: false,
                cost: true,
                default_price: true,
                labels: true,
                last_modified_at: false,
            },
        },

        // Pricebook Items - Miscellaneous
        pricebook_items_miscellaneous_cards__active_all_active: {
            sorting: [{ id: "description", desc: false }],
            attributeVisibility: {
                description: true,
                subtype_label: true,
                cost: true,
                default_price: true,
                labels: true,
                last_modified_at: false,
            },
        },
        pricebook_items_miscellaneous_cards__active_review: {
            sorting: [{ id: "description", desc: false }],
            attributeVisibility: {
                description: true,
                subtype_label: true,
                cost: true,
                default_price: true,
                labels: true,
                last_modified_at: false,
            },
        },
        pricebook_items_miscellaneous_cards__active_confirmed: {
            sorting: [{ id: "description", desc: false }],
            attributeVisibility: {
                description: true,
                subtype_label: true,
                cost: true,
                default_price: true,
                labels: true,
                last_modified_at: false,
            },
        },
        pricebook_items_miscellaneous_cards__inactive_all_inactive: {
            sorting: [
                { id: "confirmed", desc: false },
                { id: "description", desc: false },
            ],
            attributeVisibility: {
                description: true,
                subtype_label: true,
                cost: true,
                default_price: true,
                labels: true,
                last_modified_at: false,
            },
        },

        // Pricebook Items - Discounts
        pricebook_items_discounts_cards__active_all_active: {
            sorting: [
                { id: "confirmed", desc: false },
                { id: "description", desc: false },
            ],
            attributeVisibility: {
                description: true,
                subtype_label: true,
                summary_template: true,
                default_price: true,
                labels: true,
                last_modified_at: false,
            },
        },
        pricebook_items_discounts_cards__active_review: {
            sorting: [{ id: "description", desc: false }],
            attributeVisibility: {
                description: true,
                subtype_label: true,
                summary_template: true,
                default_price: true,
                labels: true,
                last_modified_at: false,
            },
        },
        pricebook_items_discounts_cards__active_confirmed: {
            sorting: [{ id: "description", desc: false }],
            attributeVisibility: {
                description: true,
                subtype_label: true,
                summary_template: true,
                default_price: true,
                labels: true,
                last_modified_at: false,
            },
        },
        pricebook_items_discounts_cards__inactive_all_inactive: {
            sorting: [
                { id: "confirmed", desc: false },
                { id: "description", desc: false },
            ],
            attributeVisibility: {
                description: true,
                subtype_label: true,
                summary_template: true,
                default_price: true,
                labels: true,
                last_modified_at: false,
            },
        },

        // Pricebook Items - Tax Rates
        pricebook_items_tax_rates_cards__active_all_active: {
            sorting: [
                { id: "confirmed", desc: false },
                { id: "description", desc: false },
            ],
            attributeVisibility: {
                description: true,
                summary_template: true,
                default_price: true,
                labels: true,
                last_modified_at: true,
            },
        },
        pricebook_items_tax_rates_cards__active_review: {
            sorting: [{ id: "description", desc: false }],
            attributeVisibility: {
                description: true,
                summary_template: true,
                default_price: true,
                labels: true,
                last_modified_at: true,
            },
        },
        pricebook_items_tax_rates_cards__active_confirmed: {
            sorting: [{ id: "description", desc: false }],
            attributeVisibility: {
                description: true,
                summary_template: true,
                default_price: true,
                labels: true,
                last_modified_at: true,
            },
        },
        pricebook_items_tax_rates_cards__inactive_all_inactive: {
            sorting: [
                { id: "confirmed", desc: false },
                { id: "description", desc: false },
            ],
            attributeVisibility: {
                description: true,
                summary_template: true,
                default_price: true,
                labels: true,
                last_modified_at: true,
            },
        },
    },
}

export default defaultCardsViewConfig
